// src/features/reports/insightRules.js
//
// INSIGHT-CALLOUT: Pure rule functions that derive insight objects from
// already-loaded report data. No fetches, no side effects.
//
// Exported:
//   computeOpenRolesInsight(markets, kpis, threshold?)
//   computeHeadcountGrowthInsight(offices, snapByLocMonth, allMonths, workerType, threshold?)

import {
  computeSpaceFit,
  SPACE_FIT_VERDICT_OVER,
  SPACE_FIT_VERDICT_DENSE,
} from "../../utils/spaceFitVerdict.js";

// ─── Open-Roles pressure insight ─────────────────────────────────────────────

/**
 * Compute an open-roles pressure insight from the already-loaded report data.
 *
 * Mirrors the `inPressure` useMemo in OpenRolesReport exactly so the two
 * sources always agree.
 *
 * @param {object[]} markets   - markets array from the open-roles API response
 * @param {{ officeBased: number }} kpis - kpis object from the same response
 * @param {number} [threshold=0.20] - minimum pressurePct to fire (fraction, e.g. 0.20 = 20%)
 * @returns {object|null} insight object or null when below threshold
 */
export function computeOpenRolesInsight(markets, kpis, threshold = 0.20) {
  if (!Array.isArray(markets) || !kpis) return null;
  const officeBased = typeof kpis.officeBased === "number" ? kpis.officeBased : 0;
  if (officeBased <= 0) return null;

  const tightMarkets = markets.filter((r) => {
    const { verdict } = computeSpaceFit(r.totalDerivedSqft, r.totalSeats, r.totalPeople);
    return verdict === SPACE_FIT_VERDICT_OVER || verdict === SPACE_FIT_VERDICT_DENSE;
  });

  const inPressure = Math.round(
    tightMarkets.reduce((sum, r) => sum + (r.openRoles ?? 0), 0) * 10
  ) / 10;

  const pressurePct = inPressure / officeBased;
  if (pressurePct < threshold) return null;

  const pctDisplay = Math.round(pressurePct * 100);
  const roomCount  = Math.max(0, Math.round(officeBased) - Math.round(inPressure));

  return {
    type:    "pressure",
    metric:  `${pctDisplay}%`,
    headline: `${pctDisplay}% of office-based hires land in over-capacity markets`,
    soWhat:  `${Math.round(inPressure)} of your ${Math.round(officeBased)} office-based roles are hiring into markets that already exceed seat capacity or density limits. This will compound space pressure before any new lease closes.`,
    viz: {
      kind:          "splitBar",
      pressureCount: Math.round(inPressure),
      roomCount,
    },
    tightMarkets: tightMarkets.map((r) => r.market ?? r.name ?? "Unknown"),
    deepLink: "?report=open-roles&view=market",
    action:   "Review pressure markets",
  };
}

// ─── Headcount growth insight ─────────────────────────────────────────────────

/**
 * Minimum absolute headcount a market must have at the start of the window
 * before a growth callout can fire.  A market going from 2→3 people is a +50%
 * swing that carries no real signal; this floor suppresses those.
 * Callers may supply their own value via the minHcFloor parameter.
 */
export const MIN_HC_FLOOR = 10;

/**
 * Get the headcount value for a snapshot row and workerType.
 * Matches the getValue() function in HeadcountTrendReport.jsx.
 * @internal
 */
function _getValue(snap, workerType) {
  if (!snap) return null;
  switch (workerType) {
    case "ft":         return snap.ft ?? null;
    case "contractor": return (snap.cw ?? 0) + (snap.ic ?? 0);
    default:           return snap.total ?? snap.officeSum ?? null;
  }
}

/**
 * Compute a headcount-growth insight from the already-loaded HC trend data.
 *
 * Groups offices by market, computes first-to-last HC change, and looks for
 * the market with the strongest sustained growth (≥ threshold) whose offices
 * are also Over-Capacity or Dense according to SpaceFit (when sqft/seats
 * are available on the office objects; otherwise growth alone gates the insight).
 *
 * @param {object[]} offices         - offices array from the HC trend API response
 * @param {Map<string,object>} snapByLocMonth - Map keyed `${locationId}|${YYYY-MM}`
 * @param {string[]} allMonths       - ordered YYYY-MM strings (from buildAllMonths)
 * @param {string} [workerType="all"] - "all" | "ft" | "contractor"
 * @param {number} [threshold=0.10]  - minimum fractional growth to fire (0.10 = 10%)
 * @returns {object|null} insight object or null when below threshold
 */
export function computeHeadcountGrowthInsight(
  offices,
  snapByLocMonth,
  allMonths,
  workerType = "all",
  threshold = 0.10,
  minHcFloor = MIN_HC_FLOOR,
) {
  if (!Array.isArray(offices) || !offices.length) return null;
  if (!Array.isArray(allMonths) || allMonths.length < 2) return null;
  if (!(snapByLocMonth instanceof Map)) return null;

  const firstMonth = allMonths[0];
  const lastMonth  = allMonths[allMonths.length - 1];

  // Group offices by market and sum HC across the actual loaded data window.
  // allMonths may be a padded calendar range (e.g. Jan→Dec) but real snapshots
  // may only start mid-year. Scanning all months per office finds the actual
  // first and last data points rather than anchoring to the padded extremes.
  const marketMap = new Map();

  for (const office of offices) {
    const mkt = office.market || office.locationId;
    if (!marketMap.has(mkt)) {
      marketMap.set(mkt, {
        market:   mkt,
        alias:    office.market || office.locationId,
        offices:  [],
        firstHC:  0,
        lastHC:   0,
        hasFirst: false,
        hasLast:  false,
      });
    }
    const entry = marketMap.get(mkt);
    entry.offices.push(office);

    // Scan all months to find the actual first and last data points.
    let officeFirstVal = null;
    let officeLastVal  = null;
    for (const month of allMonths) {
      const snap = snapByLocMonth.get(`${office.locationId}|${month}`);
      const val  = _getValue(snap, workerType);
      if (val != null) {
        if (officeFirstVal === null) officeFirstVal = val;
        officeLastVal = val;
      }
    }

    if (officeFirstVal != null) { entry.firstHC += officeFirstVal; entry.hasFirst = true; }
    if (officeLastVal  != null) { entry.lastHC  += officeLastVal;  entry.hasLast  = true; }
  }

  // Find the market with the highest fractional growth that exceeds threshold.
  let bestMarket = null;
  let bestGrowth = -Infinity;

  for (const entry of marketMap.values()) {
    if (!entry.hasFirst || !entry.hasLast) continue;
    if (entry.firstHC <= 0) continue;
    if (entry.firstHC < minHcFloor) continue;
    const growth = (entry.lastHC - entry.firstHC) / entry.firstHC;
    if (growth < threshold) continue;

    // SpaceFit check: market must have at least one Over/Dense office.
    // When offices carry no sqft+seats, tightness cannot be confirmed → skip.
    // HC trend offices carry sqft + installedSeats (added in server response).
    const officeList = entry.offices;
    const hasSpaceData = officeList.some(
      (o) =>
        (o.sqft != null || o.totalDerivedSqft != null) &&
        (o.installedSeats != null || o.seats != null || o.totalSeats != null),
    );
    if (!hasSpaceData) continue;
    const hasOverDense = officeList.some((o) => {
      const sqft   = o.totalDerivedSqft ?? o.sqft ?? null;
      const seats  = o.totalSeats ?? o.installedSeats ?? o.seats ?? null;
      const people = o.totalPeople ?? o.latestHeadcount ?? entry.lastHC / officeList.length;
      const { verdict } = computeSpaceFit(sqft, seats, people);
      return verdict === SPACE_FIT_VERDICT_OVER || verdict === SPACE_FIT_VERDICT_DENSE;
    });
    if (!hasOverDense) continue;

    if (growth > bestGrowth) {
      bestGrowth  = growth;
      bestMarket  = entry;
    }
  }

  if (!bestMarket) return null;

  const growthPct = Math.round(bestGrowth * 100);

  // Build a minimal trend sparkline (first + last values; more points if available).
  const points = allMonths.map((m) => {
    let total = 0;
    let hasAny = false;
    for (const office of bestMarket.offices) {
      const snap = snapByLocMonth.get(`${office.locationId}|${m}`);
      const v    = _getValue(snap, workerType);
      if (v != null) { total += v; hasAny = true; }
    }
    return hasAny ? total : null;
  }).filter((v) => v != null);

  const fmtM = (m) => {
    if (!m) return "";
    const [, mm] = m.split("-");
    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return MONTHS[(parseInt(mm, 10) - 1) % 12] ?? m;
  };

  return {
    type:    "growth",
    metric:  `+${growthPct}%`,
    headline: `${bestMarket.alias} headcount up ${growthPct}% over the loaded window`,
    soWhat:  `${bestMarket.alias} grew from ${bestMarket.firstHC} to ${bestMarket.lastHC} assigned seats over this period. If office capacity has not kept pace, this market may need a space plan update.`,
    viz: {
      kind:       "trend",
      points,
      startLabel: fmtM(firstMonth),
      endLabel:   fmtM(lastMonth),
    },
    marketName: bestMarket.alias,
    deepLink:   `?report=headcount&market=${encodeURIComponent(bestMarket.alias)}`,
    action:     "Model space",
  };
}
